import { ConversationPublic, ConversationWithMessages, SaveConversationRequest, MessagePublic } from "./models";

export interface CreateConversationRequest {
    title?: string;
}

export interface AddMessageRequest {
    role: "user" | "assistant";
    content: string;
}

class ChatHistoryService {
    private userId: string | null = null;

    setUserId(userId: string) {
        this.userId = userId;
    }

    private getHeaders(): HeadersInit {
        const headers: HeadersInit = {
            "Content-Type": "application/json",
        };
        if (this.userId) {
            headers["x-clerk-user-id"] = this.userId;
        }
        return headers;
    }

    async createConversation(request: CreateConversationRequest = {}): Promise<ConversationPublic> {
        const response = await fetch("/api/conversations", {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            throw new Error(`Failed to create conversation: ${response.status}`);
        }

        return response.json();
    }

    async addMessage(conversationId: string, message: AddMessageRequest): Promise<MessagePublic> {
        const response = await fetch(`/api/conversations/${conversationId}/messages`, {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify(message),
        });

        if (!response.ok) {
            throw new Error(`Failed to add message: ${response.status}`);
        }

        return response.json();
    }

    async listConversations(limit: number = 50, offset: number = 0): Promise<ConversationPublic[]> {
        const response = await fetch(`/api/conversations?limit=${limit}&offset=${offset}`, {
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch conversations: ${response.status}`);
        }

        return response.json();
    }

    async getConversation(conversationId: string): Promise<ConversationWithMessages> {
        const response = await fetch(`/api/conversations/${conversationId}`, {
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error("Conversation not found");
            }
            throw new Error(`Failed to fetch conversation: ${response.status}`);
        }

        return response.json();
    }

    async saveConversation(request: SaveConversationRequest): Promise<ConversationPublic> {
        const response = await fetch("/api/conversations/save", {
            method: "POST",
            headers: this.getHeaders(),
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            throw new Error(`Failed to save conversation: ${response.status}`);
        }

        return response.json();
    }

    async updateConversationTitle(conversationId: string, title: string): Promise<ConversationPublic> {
        const response = await fetch(`/api/conversations/${conversationId}`, {
            method: "PUT",
            headers: this.getHeaders(),
            body: JSON.stringify({ title }),
        });

        if (!response.ok) {
            throw new Error(`Failed to update conversation: ${response.status}`);
        }

        return response.json();
    }

    async deleteConversation(conversationId: string): Promise<void> {
        const response = await fetch(`/api/conversations/${conversationId}`, {
            method: "DELETE",
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            throw new Error(`Failed to delete conversation: ${response.status}`);
        }
    }
}

export const chatHistoryService = new ChatHistoryService();
